import { useCallback, useEffect, useState } from 'react'
import { Icon } from '../ui/Icon'
import {
  applyTheme,
  nextPreference,
  readPreference,
  resolveTheme,
  setPreference,
  subscribeToSystemTheme,
  THEME_ICONS,
  THEME_LABELS,
  type ResolvedTheme,
  type ThemePreference,
} from '../../styles/theme.ts'

/**
 * The light/dark control, in the top bar beside the bell and the cog.
 *
 * Chrome furniture, not a route. It is a real button with a real handler, which is the standard
 * `TopBar.tsx` already holds itself to: the bell and the cog used to be bare `<span>`s and were
 * made buttons precisely because "a control that looks live and is not" is the thing CONSOLES.md
 * 2.3 forbids in its own domain.
 *
 * ---------------------------------------------------------------------------------------------
 * ONE BUTTON, THREE STATES, AND HOW IT SAYS SO WITHOUT A MENU.
 *
 * Cycling system → light → dark → system on click. The icon shows the PREFERENCE (monitor, sun,
 * moon), not the resolved mode, because the resolved mode is already visible -- the whole page is
 * showing it. What a person cannot see is whether they are following the OS or have pinned a
 * choice, and that is the state worth spending the icon on.
 *
 * The `aria-label` carries both halves, and it has to: on 'system' the icon alone cannot say
 * which mode the system chose, and a screen-reader user gets none of the page's visual answer.
 * So the label reads "Appearance: System (dark). Change to Light" -- current state, what the
 * system resolved to, and what the press will do.
 *
 * A three-way radio group in the settings panel would be more explicit and is where this goes if
 * one button ever proves too clever. It is one button for now because the settings panel is two
 * clicks away and this is a control people flip, not configure.
 * ---------------------------------------------------------------------------------------------
 *
 * WHY THE STATE IS NOT LIFTED. `ConsoleChrome` mounts this and nothing else needs to know the
 * theme -- the tokens do the work, in CSS, on `<html>`. Putting a provider above it would be a
 * context whose only consumer is a single button. The DOM attribute IS the shared state, and the
 * one thing that genuinely reads it is a stylesheet.
 */
export function ThemeToggle() {
  const [preference, setLocalPreference] = useState<ThemePreference>(() => readPreference())
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readPreference()))

  /*
   * Re-assert on mount. `index.html` has already stamped the attribute before first paint, so
   * this is not what makes the page render correctly -- it is what makes React's state and the
   * DOM agree when they were produced by two different pieces of code. Without it a stale
   * attribute (a bfcache restore, a hot reload, a test that mounted twice) survives until the
   * first click.
   */
  useEffect(() => {
    setResolved(applyTheme(preference))
  }, [preference])

  /*
   * Follow the OS while the preference is 'system'. The subscription is unconditional -- see
   * `subscribeToSystemTheme` for why tearing it down when the preference is explicit has a bug
   * in it -- so the guard is here, at the point of use, where the current preference is known.
   */
  useEffect(
    () =>
      subscribeToSystemTheme((system) => {
        if (preference !== 'system') return
        setResolved(system)
        applyTheme('system')
      }),
    [preference],
  )

  const advance = useCallback(() => {
    const next = nextPreference(preference)
    setLocalPreference(next)
    setResolved(setPreference(next))
  }, [preference])

  const upcoming = nextPreference(preference)
  // On 'system' the label names what the system resolved to, because the icon cannot. On an
  // explicit choice that parenthetical would just repeat the state, so it is left off.
  const state = preference === 'system' ? `System (${resolved})` : THEME_LABELS[preference]

  return (
    <button
      type="button"
      className="icon-btn theme-toggle"
      /*
       * Deliberately NOT marked `data-popover-trigger`, unlike the bell and the cog beside it.
       * That marker means "this button owns a panel, so an outside-click close would double up
       * with its own toggle" (see Popover.tsx). This button owns no panel, and carrying the
       * marker would make an open notifications panel survive a press here -- the theme would
       * change behind a panel the person had visibly moved away from. Left unmarked, both things
       * happen in the right order: `mousedown` closes the panel, `click` advances the theme.
       */
      data-theme-preference={preference}
      aria-label={`Appearance: ${state}. Change to ${THEME_LABELS[upcoming]}`}
      title={`Appearance: ${state}`}
      onClick={advance}
    >
      <Icon name={THEME_ICONS[preference]} size={17} />
    </button>
  )
}
